/**
 * Store Preferences Card Component
 * Zipcode input, home store selection, and road trip stores
 */

import { useState } from 'react';
import {
  MapPin,
  Search,
  Home,
  Car,
  Check,
  Loader2,
  Info,
  Store,
} from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { UseStorePreferencesReturn, StoreData } from '../types';
import { STORE_PREFS_DEFAULTS } from '../utils/constants';
import { formatDistance, formatStoreAddress } from '../utils/helpers';

interface StorePreferencesCardProps {
  storePrefs: UseStorePreferencesReturn;
}

function StoreCard({
  store,
  isHomeStore,
  onSetHomeStore,
  isSaving,
}: {
  store: StoreData;
  isHomeStore?: boolean;
  onSetHomeStore?: () => void;
  isSaving?: boolean;
}) {
  const { t } = useLanguage();

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
        isHomeStore
          ? 'bg-primary-50 border-primary-200'
          : 'bg-gray-50 border-gray-200 hover:border-gray-300'
      }`}
    >
      <div
        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
          isHomeStore ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
        }`}
      >
        <Store className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900">IKEA {store.name}</div>
        <div className="text-sm text-gray-500">{formatStoreAddress(store.city, store.state)}</div>
        {store.distance !== undefined && (
          <div className="text-xs text-gray-400 mt-0.5">
            <MapPin className="w-3 h-3 inline-block mr-1" />
            {formatDistance(store.distance)}
          </div>
        )}
      </div>
      {isHomeStore ? (
        <div className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-full text-sm font-medium">
          <Check className="w-4 h-4" />
          {t('profile.stores.homeStore', 'Home Store')}
        </div>
      ) : onSetHomeStore ? (
        <button
          onClick={onSetHomeStore}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-primary-600 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t('profile.stores.setAsHome', 'Set as Home')
          )}
        </button>
      ) : null}
    </div>
  );
}

export function StorePreferencesCard({ storePrefs }: StorePreferencesCardProps) {
  const { t } = useLanguage();
  const [showTooltip, setShowTooltip] = useState<'distance' | 'threshold' | null>(null);

  const {
    preferences,
    nearbyStores,
    isLoading,
    isSaving,
    error,
    findStores,
    setHomeStore,
    savePreferences,
    localZipcode,
    setLocalZipcode,
    localMaxDistance,
    setLocalMaxDistance,
    localBulkThreshold,
    setLocalBulkThreshold,
  } = storePrefs;

  const handleFindStores = () => {
    if (localZipcode.length === 5) {
      findStores(localZipcode);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFindStores();
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <MapPin className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('profile.stores.title', 'Store Preferences')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('profile.stores.subtitle', 'Find IKEA stores near you')}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Zipcode Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('profile.stores.yourZipcode', 'Your Zipcode')}
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={localZipcode}
              onChange={(e) => setLocalZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              onKeyDown={handleKeyDown}
              placeholder="12345"
              maxLength={5}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
            />
            <button
              onClick={handleFindStores}
              disabled={localZipcode.length !== 5 || isLoading}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  {t('profile.stores.findStores', 'Find Stores')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Home Store */}
        {preferences?.home_store && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Home className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {t('profile.stores.homeStoreSection', 'Home Store')}
              </span>
            </div>
            <StoreCard
              store={preferences.home_store}
              isHomeStore={true}
            />
          </div>
        )}

        {/* Road Trip Stores */}
        {nearbyStores.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Car className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {t('profile.stores.roadTripStores', 'Road Trip Stores')} ({nearbyStores.length})
              </span>
            </div>

            {/* Distance Slider */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    {t('profile.stores.maxDistance', 'Maximum Distance')}
                  </span>
                  <button
                    type="button"
                    className="relative"
                    onMouseEnter={() => setShowTooltip('distance')}
                    onMouseLeave={() => setShowTooltip(null)}
                    aria-label={t('profile.stores.distanceInfo', 'Maximum distance info')}
                  >
                    <Info className="w-4 h-4 text-gray-400" />
                    {showTooltip === 'distance' && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded-lg z-10">
                        {t('profile.stores.distanceTooltip', 'Only show stores within this distance from your home')}
                      </div>
                    )}
                  </button>
                </div>
                <span className="text-sm font-semibold text-primary-600">
                  {localMaxDistance} {t('profile.stores.miles', 'miles')}
                </span>
              </div>
              <input
                type="range"
                min={STORE_PREFS_DEFAULTS.MAX_DISTANCE_MIN}
                max={STORE_PREFS_DEFAULTS.MAX_DISTANCE_MAX}
                step={STORE_PREFS_DEFAULTS.MAX_DISTANCE_STEP}
                value={localMaxDistance}
                onChange={(e) => setLocalMaxDistance(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
            </div>

            {/* Threshold Slider */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    {t('profile.stores.roadTripThreshold', 'Road Trip Threshold')}
                  </span>
                  <button
                    type="button"
                    className="relative"
                    onMouseEnter={() => setShowTooltip('threshold')}
                    onMouseLeave={() => setShowTooltip(null)}
                    aria-label={t('profile.stores.thresholdInfo', 'Road trip threshold info')}
                  >
                    <Info className="w-4 h-4 text-gray-400" />
                    {showTooltip === 'threshold' && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2 bg-gray-900 text-white text-xs rounded-lg z-10">
                        {t('profile.stores.thresholdTooltip', 'Notify when a store has this many items from your wishlist')}
                      </div>
                    )}
                  </button>
                </div>
                <span className="text-sm font-semibold text-primary-600">
                  {localBulkThreshold} {t('profile.stores.items', 'items')}
                </span>
              </div>
              <input
                type="range"
                min={STORE_PREFS_DEFAULTS.BULK_THRESHOLD_MIN}
                max={STORE_PREFS_DEFAULTS.BULK_THRESHOLD_MAX}
                step={STORE_PREFS_DEFAULTS.BULK_THRESHOLD_STEP}
                value={localBulkThreshold}
                onChange={(e) => setLocalBulkThreshold(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
            </div>

            {/* Nearby Stores List */}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {nearbyStores.map((store) => (
                <StoreCard
                  key={store.id}
                  store={store}
                  onSetHomeStore={() => setHomeStore(store.id)}
                  isSaving={isSaving}
                />
              ))}
            </div>

            {/* Save Button */}
            <button
              onClick={savePreferences}
              disabled={isSaving}
              className="mt-4 w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 font-medium"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('profile.stores.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  {t('profile.stores.savePreferences', 'Save Preferences')}
                </>
              )}
            </button>
          </div>
        )}

        {/* Empty state */}
        {!preferences?.zipcode && !isLoading && (
          <div className="text-center py-8 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('profile.stores.enterZipcode', 'Enter your zipcode to find nearby stores')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
